import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font
} from "@react-pdf/renderer";
import ctbLogo from "../../assets/ctbLogoBlue.png";
import type { SowFields } from "../../utils/api/endpoints/sow";
import { getMethodologiesForTargets } from "../../utils/sowMethodologies";

// ─── Font Setup (Standard PDF built-in fonts for 100% reliable rendering) ───
Font.registerHyphenationCallback((word) => [word]);

// ─── Constants for 100% deterministic Yoga layout sizing ─────────────────────
const PAGE_WIDTH = 507;
const COVER_WIDTH = 495;

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  coverPage: {
    paddingTop: 60,
    paddingBottom: 40,
    paddingHorizontal: 50,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    color: "#0f172a",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center"
  },
  coverContent: {
    alignItems: "center",
    textAlign: "center",
    marginTop: 40,
    width: COVER_WIDTH
  },
  coverLogo: {
    width: 170,
    height: 50,
    alignSelf: "center",
    objectFit: "contain",
    marginBottom: 36
  },
  coverCategory: {
    fontSize: 9.5,
    fontFamily: "Helvetica-Bold",
    color: "#2563eb",
    letterSpacing: 1.5,
    textTransform: "uppercase",
    marginBottom: 10,
    width: COVER_WIDTH,
    textAlign: "center"
  },
  coverTitle: {
    fontSize: 28,
    fontFamily: "Helvetica-Bold",
    color: "#0f172a",
    marginBottom: 12,
    width: COVER_WIDTH,
    textAlign: "center"
  },
  coverSubtitle: {
    fontSize: 11,
    fontFamily: "Helvetica",
    color: "#475569",
    lineHeight: 1.4,
    marginBottom: 36,
    width: COVER_WIDTH - 60,
    textAlign: "center"
  },
  coverMetaCard: {
    alignItems: "center",
    width: 380,
    backgroundColor: "#f8fafc",
    borderLeftWidth: 3,
    borderLeftColor: "#2563eb",
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 20,
    marginTop: 10
  },
  coverMetaRow: {
    flexDirection: "row",
    paddingVertical: 3,
    width: 340,
    alignItems: "center"
  },
  coverMetaLabel: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#475569",
    width: 130,
    textAlign: "right",
    paddingRight: 10
  },
  coverMetaValue: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#0f172a",
    textAlign: "left",
    flex: 1
  },
  coverMetaBadge: {
    fontSize: 8.5,
    color: "#166534",
    backgroundColor: "#dcfce7",
    fontFamily: "Helvetica-Bold",
    paddingHorizontal: 6,
    paddingVertical: 1.5,
    borderRadius: 4,
    textAlign: "center"
  },
  coverFooterText: {
    fontSize: 8,
    fontFamily: "Helvetica",
    color: "#94a3b8",
    textAlign: "center",
    width: COVER_WIDTH
  },
  page: {
    paddingTop: 36,
    paddingBottom: 52,
    paddingHorizontal: 44,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#334155",
    lineHeight: 1.45
  },
  // Inner Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    width: PAGE_WIDTH
  },
  headerLine: {
    height: 1,
    width: PAGE_WIDTH,
    backgroundColor: "#cbd5e1",
    marginBottom: 16
  },
  logo: { width: 90, height: 26, objectFit: "contain" },
  headerRightText: {
    fontFamily: "Helvetica",
    fontSize: 8,
    color: "#64748b"
  },
  docTitleBanner: {
    alignItems: "center",
    marginTop: 6,
    marginBottom: 16,
    width: PAGE_WIDTH
  },
  docTitleText: {
    fontFamily: "Helvetica-Bold",
    fontSize: 16,
    color: "#1e3a8a",
    textAlign: "center",
    width: PAGE_WIDTH
  },
  // Inner Footer
  footer: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    height: 15,
    fontSize: 7.5,
    fontFamily: "Helvetica",
    color: "#64748b",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 44
  },
  footerLeft: {
    textAlign: "left",
    flex: 1
  },
  footerRight: {
    textAlign: "right",
    flex: 1
  },
  // Section heading
  sectionHeading: {
    fontFamily: "Helvetica-Bold",
    fontSize: 12,
    color: "#0f172a",
    marginTop: 14,
    marginBottom: 6,
    width: PAGE_WIDTH
  },
  subHeading: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10.5,
    color: "#16a34a",
    marginTop: 12,
    marginBottom: 6,
    width: PAGE_WIDTH
  },
  assetTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9.5,
    color: "#1e3a8a",
    marginTop: 10,
    marginBottom: 4,
    width: PAGE_WIDTH
  },
  body: { marginBottom: 6, color: "#334155", fontSize: 8.5, lineHeight: 1.45, width: PAGE_WIDTH, wordBreak: "break-word" },
  bullet: { flexDirection: "row", marginBottom: 3, paddingLeft: 8, width: PAGE_WIDTH },
  bulletDot: { width: 12, color: "#16a34a", fontSize: 8 },
  bulletText: { color: "#334155", fontSize: 8.5, width: PAGE_WIDTH - 20, wordBreak: "break-word" },
  // Tables
  tableContainer: {
    marginTop: 6,
    marginBottom: 10,
    width: PAGE_WIDTH
  },
  tableLine: {
    height: 1,
    width: PAGE_WIDTH,
    backgroundColor: "#475569"
  },
  tableText: { fontSize: 8, color: "#0f172a", lineHeight: 1.3 },
  tableTextHeader: { fontSize: 8, fontFamily: "Helvetica-Bold", color: "#ffffff", lineHeight: 1.3 }
});

// ─── Sub-components ───────────────────────────────────────────────────────────

const Bullet = ({ text }: { text: string }) => (
  <View style={s.bullet} wrap={false}>
    <Text style={s.bulletDot}>•</Text>
    <View style={{ width: PAGE_WIDTH - 20 }}>
      <Text style={s.bulletText}>{text != null && String(text).trim() !== "" ? String(text) : " "}</Text>
    </View>
  </View>
);

const SectionHeading = ({ children }: { children?: React.ReactNode }) => (
  <Text style={s.sectionHeading}>{children != null && String(children).trim() !== "" ? String(children) : " "}</Text>
);

const SubHeading = ({ children }: { children?: React.ReactNode }) => (
  <Text style={s.subHeading}>{children != null && String(children).trim() !== "" ? String(children) : " "}</Text>
);

const Body = ({ children, style }: { children?: React.ReactNode; style?: any }) => (
  <Text style={[s.body, style]}>{children != null && String(children).trim() !== "" ? String(children) : " "}</Text>
);

/**
 * Safely prepares text for table cells in @react-pdf/renderer.
 * Preserves normal words, domains, and URLs without inserting unwanted spaces into
 * regular text (e.g. "Production", "Confirmed", "test.com").
 * Only breaks exceptionally long continuous tokens that exceed the cell width capacity.
 */
function wrapTableCellText(val: any, cellWidthPt: number): string {
  if (val == null) return " ";
  const raw = String(val).trim();
  if (!raw) return " ";

  // Cell padding: 5pt left + 5pt right
  const usableWidth = Math.max(20, cellWidthPt - 10);
  // In Helvetica 8pt (used for table text), average character width is ~4.3pt.
  const approxMaxChars = Math.max(10, Math.floor(usableWidth / 4.3));

  // Step 1: Ensure commas followed by text have a space for clean readability
  const normalized = raw.replace(/,\s*/g, ", ");

  // Step 2: Split by space to evaluate each token
  const words = normalized.split(/\s+/);
  const formattedWords: string[] = [];

  for (const word of words) {
    if (!word) continue;
    // Keep words/tokens intact if they fit inside the estimated character limit of the cell width
    if (word.length <= approxMaxChars) {
      formattedWords.push(word);
    } else {
      // For exceptionally long contiguous words/URLs without spaces, chunk into segments of approxMaxChars
      for (let i = 0; i < word.length; i += approxMaxChars) {
        formattedWords.push(word.slice(i, i + approxMaxChars));
      }
    }
  }

  return formattedWords.join(" ");
}

const TableRow = ({
  cells,
  widths,
  header,
  last
}: {
  cells: (string | undefined | null)[];
  widths?: number[];
  header?: boolean;
  last?: boolean;
}) => {
  const safeCells = cells && cells.length > 0 ? cells : [" "];
  const safeWidths = widths && widths.length === safeCells.length ? widths : safeCells.map(() => 1);
  const totalWeight = safeWidths.reduce((a, b) => a + (Number(b) || 1), 0) || 1;

  return (
    <View wrap={false} style={{ width: PAGE_WIDTH, flexDirection: "column" }}>
      <View style={{ flexDirection: "row", width: PAGE_WIDTH, backgroundColor: header ? "#334155" : "#ffffff" }}>
        {safeCells.map((c, i) => {
          const wWeight = Number(safeWidths[i]) > 0 ? Number(safeWidths[i]) : 1;
          const cellWidth = Math.floor((wWeight / totalWeight) * PAGE_WIDTH * 100) / 100;
          const textStyle = header ? s.tableTextHeader : s.tableText;
          const formatted = header ? (c != null && String(c).trim() !== "" ? String(c) : " ") : wrapTableCellText(c, cellWidth);

          return (
            <View key={i} style={{ width: cellWidth, padding: 5, overflow: "hidden" }}>
              <Text style={textStyle}>{formatted}</Text>
            </View>
          );
        })}
      </View>
      {!last ? <View style={{ height: 1, width: PAGE_WIDTH, backgroundColor: header ? "#475569" : "#e2e8f0" }} /> : null}
    </View>
  );
};

const Table = ({ children }: { children: React.ReactNode }) => (
  <View style={s.tableContainer}>
    <View style={s.tableLine} />
    {children}
    <View style={s.tableLine} />
  </View>
);

// ─── Props & exported types ───────────────────────────────────────────────────

export type SowModule = {
  assetName: string;
  assessmentType: string;
  isGraybox: boolean;
  platform?: "android" | "ios";
  targets: string[];
  environment?: string;
  status?: string;
  description?: string;
};

export type SowPdfDocumentProps = {
  fields: SowFields;
  clientName: string;
  sowVersion: number;
  modules: SowModule[];
};

// ─── Document ─────────────────────────────────────────────────────────────────

export const SowPdfDocument: React.FC<SowPdfDocumentProps> = ({
  fields,
  clientName,
  sowVersion,
  modules
}) => {
  const today = new Date().toLocaleDateString("en-NZ", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });

  const fmtDate = (iso: string) => {
    if (!iso) return "To be confirmed";
    try {
      return new Date(iso).toLocaleDateString("en-NZ", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
    } catch {
      return iso;
    }
  };

  const currency = fields?.currency || "NZD";
  const rawPrice = fields?.price != null ? Number(String(fields.price).replace(/[^0-9.-]+/g, "")) : NaN;
  const price = !isNaN(rawPrice) && rawPrice > 0
    ? `${currency} $${rawPrice.toLocaleString()}`
    : fields?.price
    ? (String(fields.price).includes("$") ? String(fields.price) : `${currency} $${fields.price}`)
    : "To be confirmed";

  // ─── Human-readable labels for assessment types ────────────────────────────
  const ASSESSMENT_LABELS: Record<string, string> = {
    WEB_APP: "Web Application",
    WEB_APPLICATION: "Web Application",
    API: "API / Integration Service",
    API_ASSESSMENT: "API / Integration Service",
    MOBILE_APP: "Mobile Application",
    MOBILE_APPLICATION: "Mobile Application",
    ANDROID_APP: "Android Application",
    IOS_APP: "iOS Application",
    EXTERNAL_NETWORK: "External Network",
    INTERNAL_NETWORK: "Internal Network",
    AI_LLM: "AI & LLM Assessment",
    AI_SYSTEM: "AI & LLM Assessment",
    CODE_REVIEW: "Secure Code Review",
    SECURE_CODE_REVIEW: "Secure Code Review",
    CLOUD_CONFIG: "Cloud Configuration Audit",
    CLOUD_CONFIGURATION: "Cloud Configuration Audit",
    CLOUD_PENTEST: "Cloud Infrastructure Pentest",
    CLOUD_INFRASTRUCTURE: "Cloud Infrastructure Pentest",
    M365_REVIEW: "M365 Tenant Audit",
    VPN_FIREWALL_REVIEW: "VPN & Firewall Config Review",
    PENETRATION_TESTING: "Penetration Testing"
  };

  const CATEGORY_LABELS: Record<string, string> = {
    WEB_APP: "Digital Applications",
    WEB_APPLICATION: "Digital Applications",
    API: "Digital Applications",
    API_ASSESSMENT: "Digital Applications",
    MOBILE_APP: "Digital Applications",
    MOBILE_APPLICATION: "Digital Applications",
    EXTERNAL_NETWORK: "Infrastructure",
    INTERNAL_NETWORK: "Infrastructure",
    AI_LLM: "Digital Applications",
    AI_SYSTEM: "Digital Applications",
    CODE_REVIEW: "Digital Applications",
    SECURE_CODE_REVIEW: "Digital Applications",
    CLOUD_CONFIG: "Infrastructure",
    CLOUD_CONFIGURATION: "Infrastructure",
    CLOUD_PENTEST: "Infrastructure",
    CLOUD_INFRASTRUCTURE: "Infrastructure",
    M365_REVIEW: "Infrastructure",
    VPN_FIREWALL_REVIEW: "Infrastructure",
    PENETRATION_TESTING: "Digital Applications"
  };

  const normalizeTypeKey = (raw: string) => {
    const s = String(raw || "").toUpperCase().trim();
    if (s.includes("WEB")) return "WEB_APP";
    if (s.includes("API")) return "API";
    if (s.includes("MOBILE")) return "MOBILE_APP";
    if (s.includes("EXTERNAL")) return "EXTERNAL_NETWORK";
    if (s.includes("INTERNAL")) return "INTERNAL_NETWORK";
    if (s.includes("CLOUD_PENTEST") || s.includes("INFRASTRUCTURE")) return "CLOUD_PENTEST";
    if (s.includes("CLOUD")) return "CLOUD_CONFIG";
    if (s.includes("AI") || s.includes("LLM")) return "AI_LLM";
    if (s.includes("CODE")) return "CODE_REVIEW";
    if (s.includes("VPN") || s.includes("FIREWALL")) return "VPN_FIREWALL_REVIEW";
    if (s.includes("M365")) return "M365_REVIEW";
    return s || "WEB_APP";
  };

  const labelFor = (type: string) => {
    const norm = normalizeTypeKey(type);
    return ASSESSMENT_LABELS[norm] || ASSESSMENT_LABELS[type] || type?.replace(/_/g, " ") || "Web Application";
  };
  const categoryFor = (type: string) => {
    const norm = normalizeTypeKey(type);
    return CATEGORY_LABELS[norm] || CATEGORY_LABELS[type] || "Digital Applications";
  };

  // ─── Targets: from fields.targetItems (if populated), fall back to modules, then empty ───────
  const hasRealTargetItems =
    fields?.targetItems &&
    fields.targetItems.some((t: any) =>
      Boolean((t.assetName && t.assetName.trim()) || (t.targets && t.targets.trim()))
    );

  const displayTargets = hasRealTargetItems
    ? fields.targetItems
    : (modules && modules.length > 0)
    ? modules.map((m: any, idx: number) => ({
        id: `tgt-mod-${idx}`,
        assetName: m.assetName || "",
        assessmentType: m.assessmentType || "WEB_APP",
        accessType: (m.isGraybox ? "graybox" : "blackbox") as any,
        targets: Array.isArray(m.targets) ? m.targets.join(", ") : (m.targets || ""),
        environment: m.environment || "Production",
        status: m.status || "Confirmed",
        description: m.description || ""
      }))
    : (fields?.targetItems && fields.targetItems.length > 0)
    ? fields.targetItems
    : [];

  const clientNameStr = clientName || "Client";
  const sowVersionStr = String(sowVersion || (fields as any)?.version || "1.0");

  const coverEngagementType = fields?.coverEngagementType || "PENETRATION TESTING ENGAGEMENT";
  const coverDocTitle = fields?.coverDocumentTitle || "Statement of Work";

  // Deduplicated unique target names for clean display
  const autoSubtitle = Array.from(
    new Set(
      displayTargets
        .map((m: any) => m.assetName || labelFor(m.assessmentType) || m.assessmentType)
        .filter(Boolean)
    )
  ).join(" & ").toUpperCase() || "PENETRATION TESTING";

  const coverSubtitle = (fields?.coverSubtitle && fields.coverSubtitle.trim())
    ? fields.coverSubtitle.trim()
    : autoSubtitle;
  const coverPreparedFor = fields?.coverPreparedFor || clientNameStr;
  const coverPreparedBy = fields?.coverPreparedBy || "Capture The Bug Limited";
  const defaultDocRef = `055-${today.replace(/\s+/g, "").toUpperCase()}-CTB-SOW-V${sowVersionStr}`;
  const coverDocRef = (fields?.coverDocumentReference && fields.coverDocumentReference.trim())
    ? fields.coverDocumentReference.trim()
    : defaultDocRef;
  const coverVersion = fields?.coverVersionLabel || sowVersionStr;
  const coverClassification = fields?.coverClassification || "Confidential";

  const applyClassification = (text?: string): string => {
    if (!text || !text.trim()) return "";
    return text.replace(/Confidential|Restricted|Commercial In Confidence|Internal Use Only|Public/gi, coverClassification);
  };

  const coverFooterNote = (fields?.coverFooterNote && fields.coverFooterNote.trim())
    ? applyClassification(fields.coverFooterNote)
    : `capturethebug.xyz   ·   ${coverClassification}: intended recipient only`;
  const footerLeftNote = (fields?.footerReferenceNote && fields.footerReferenceNote.trim())
    ? applyClassification(fields.footerReferenceNote)
    : `${coverDocRef}   ·   ${coverClassification}`;
  const innerPageHeader = (fields?.innerPageHeaderText && fields.innerPageHeaderText.trim())
    ? applyClassification(fields.innerPageHeaderText)
    : `${coverDocTitle}   ·   ${coverClassification}`;

  return (
    <Document
      title={`CTB Statement of Work — ${clientNameStr}`}
      author="Capture The Bug Limited"
      subject="Penetration Testing Statement of Work"
    >
      {/* ─── Cover Page (White Theme) ─── */}
      <Page size="A4" style={s.coverPage}>
        <View style={s.coverContent}>
          <Image src={ctbLogo} style={s.coverLogo} />
          
          <Text style={s.coverCategory}>{coverEngagementType}</Text>
          <Text style={s.coverTitle}>{coverDocTitle}</Text>
          <Text style={s.coverSubtitle}>
            {coverSubtitle}
          </Text>

          <View style={s.coverMetaCard}>
            <View style={s.coverMetaRow}>
              <Text style={s.coverMetaLabel}>Prepared for:</Text>
              <Text style={s.coverMetaValue}>{coverPreparedFor}</Text>
            </View>
            <View style={s.coverMetaRow}>
              <Text style={s.coverMetaLabel}>Prepared by:</Text>
              <Text style={s.coverMetaValue}>{coverPreparedBy}</Text>
            </View>
            <View style={s.coverMetaRow}>
              <Text style={s.coverMetaLabel}>Document reference:</Text>
              <Text style={s.coverMetaValue}>{coverDocRef}</Text>
            </View>
            <View style={s.coverMetaRow}>
              <Text style={s.coverMetaLabel}>Version:</Text>
              <Text style={s.coverMetaValue}>{coverVersion}</Text>
            </View>
            <View style={s.coverMetaRow}>
              <Text style={s.coverMetaLabel}>Classification:</Text>
              <View style={{ flex: 1, alignItems: "flex-start" }}>
                <Text style={s.coverMetaBadge}>{coverClassification}</Text>
              </View>
            </View>
          </View>
        </View>

        <Text style={s.coverFooterText}>
          {coverFooterNote}
        </Text>
      </Page>

      {/* ─── Page 2+ (Content Pages) ─── */}
      <Page size="A4" style={s.page}>
        {/* Header */}
        <View style={s.header} fixed>
          <Image src={ctbLogo} style={s.logo} />
          <Text style={s.headerRightText}>
            {innerPageHeader}
          </Text>
        </View>
        <View style={s.headerLine} fixed />

        {/* Scope of Work Title Banner */}
        <View style={s.docTitleBanner}>
          <Text style={s.docTitleText}>Scope of Work</Text>
        </View>

        {/* ─── 1. Introduction ─── */}
        <SectionHeading>Introduction</SectionHeading>
        {fields?.introductionText && fields.introductionText.trim() ? (
          fields.introductionText.trim().split("\n\n").map((p, idx) => (
            <Body key={`intro-${idx}`}>{p.trim()}</Body>
          ))
        ) : (
          <>
            <Body>
              {`Capture The Bug (CTB) will conduct a penetration test for ${clientNameStr}, covering the agreed scope, testing approach, deliverables, responsibilities, and commercial terms. Appendix A - Rules of Engagement forms part of this Statement of Work and defines the operational conditions for testing.`}
            </Body>
            <Body>
              {`The assessment is performed remotely through the CTB PTaaS platform against the environment(s) agreed with ${clientNameStr}, using the testing approach set out in the Engagement Structure.`}
            </Body>
            <Body>
              The engagement will identify and validate security weaknesses across authorised targets and provide prioritised remediation guidance. Retesting is included as set out in this document.
            </Body>
          </>
        )}

        {/* ─── 2. Objective ─── */}
        <SectionHeading>Objective of Engagement</SectionHeading>
        {fields?.objectiveText && fields.objectiveText.trim() ? (
          fields.objectiveText.trim().split("\n\n").map((p, idx) => (
            <Body key={`obj-${idx}`}>{p.trim()}</Body>
          ))
        ) : (
          <>
            <Body>
              {`The objective is to identify and validate material vulnerabilities within ${clientNameStr}'s authorised targets as defined in this Statement of Work. Particular attention will be given to authentication and session management, authorisation and access-control enforcement, the integrity of core business workflows, and the security of any privileged or sensitive functionality within scope. CTB will prioritise these attack classes during manual testing where they are relevant to the identified attack surface, while maintaining comprehensive coverage of the agreed scope.`}
            </Body>
            <Body>
              {`The engagement also provides ${clientNameStr} with an independent assessment of its externally exposed attack surface to support internal risk-management activities. This assessment does not constitute a formal compliance audit, certification, or attestation, and no such outcome is offered or implied.`}
            </Body>
          </>
        )}

        {/* ─── Scope of Engagement ─── */}
        <View style={s.docTitleBanner}>
          <Text style={s.docTitleText}>Scope of Engagement</Text>
        </View>

        <SubHeading>Assets and Coverage</SubHeading>
        <Table>
          <TableRow cells={["Asset", "Asset Category", "Asset Type(s)"]} header widths={[2, 2, 2]} />
          {displayTargets.length > 0 ? displayTargets.map((item: any, i: number) => (
            <TableRow
              key={i}
              widths={[2, 2, 2]}
              cells={[
                item.assetName || "",
                categoryFor(item.assessmentType),
                labelFor(item.assessmentType)
              ]}
              last={i === displayTargets.length - 1}
            />
          )) : (
            <TableRow widths={[2, 2, 2]} cells={["", "", ""]} last />
          )}
        </Table>

        {/* Per-asset descriptions — editable via § 3 Asset Description field */}
        {displayTargets.map((item: any, i: number) => (
          <View key={i} style={{ width: PAGE_WIDTH }} wrap={false}>
            {(item.assetName || item.asset_name) ? (
              <Text style={s.assetTitle}>{item.assetName || item.asset_name}</Text>
            ) : null}
            {item.description ? (
              <Body>{item.description}</Body>
            ) : null}
          </View>
        ))}

        <SubHeading>Authorised Targets</SubHeading>
        <Table>
          <TableRow cells={["Asset", "Type", "Target", "Environment", "Status"]} header widths={[1.5, 1.2, 2.5, 1.0, 0.8]} />
          {displayTargets.length > 0 ? displayTargets.map((item: any, i: number) => (
            <TableRow
              key={i}
              widths={[1.5, 1.2, 2.5, 1.0, 0.8]}
              cells={[
                item.assetName || "",
                labelFor(item.assessmentType),
                Array.isArray(item.targets) ? item.targets.join(", ") : (item.targets || ""),
                item.environment || fields?.testingEnvironment || "Production",
                item.status || "Confirmed"
              ]}
              last={i === displayTargets.length - 1}
            />
          )) : (
            <TableRow widths={[1.5, 1.2, 2.5, 1.0, 0.8]} cells={["", "", "", "", ""]} last />
          )}
        </Table>

        <SubHeading>Scope Boundary and Endpoint Discovery</SubHeading>
        <Body>
          {fields?.scopeBoundaryText && fields.scopeBoundaryText.trim()
            ? fields.scopeBoundaryText
            : `Testing is limited to the formally authorised targets confirmed by ${clientNameStr} before the engagement begins. Any host, service, application, or address outside the approved target list is out of scope. Individual pages, routes, API paths, parameters, ports, subdomains, and administrative interfaces identified during testing are treated as discovery artefacts for reporting purposes only and do not independently expand the authorised legal scope.`}
        </Body>

        <SubHeading>Rules of Engagement</SubHeading>
        {fields?.rulesOfEngagementSummaryText && fields.rulesOfEngagementSummaryText.trim() ? (
          fields.rulesOfEngagementSummaryText.trim().split("\n\n").map((p, idx) => (
            <Body key={`roe-sum-${idx}`}>{p.trim()}</Body>
          ))
        ) : (
          <>
            <Body>
              Appendix A - Rules of Engagement forms part of this Statement of Work and records the authorised targets, testing environment, CTB's testing source address, in-scope accounts and named testers, confirmed testing duration, testing window and blackout periods, emergency contacts, prohibited testing activities, and the stop-testing procedure.
            </Body>
            <Body>
              Fields in Appendix A marked as "To be confirmed" depend on information that becomes available during scope validation. They will be completed and confirmed in writing by both parties before testing commences, and completion of Appendix A is a condition precedent to the commencement of testing. Where Appendix A conflicts with the body of this Statement of Work, the body prevails.
            </Body>
          </>
        )}

        {/* ─── Testing Methodology ─── */}
        <View style={s.docTitleBanner}>
          <Text style={s.docTitleText}>Testing Methodology</Text>
        </View>

        <Body>
          {fields?.testingMethodologyText && fields.testingMethodologyText.trim()
            ? fields.testingMethodologyText
            : "CTB will perform the assessment in alignment with recognised penetration testing guidance, including the Penetration Testing Execution Standard (PTES), NIST SP 800-115, the OWASP Web Security Testing Guide, the OWASP Top 10, and the OWASP API Security Top 10, where relevant to the authorised attack surface. Testing combines automated discovery and vulnerability analysis with focused manual testing and validation by experienced consultants: automated techniques map the authorised attack surface and identify candidate issues, and manual testing validates exploitability, removes false positives, investigates business-logic weaknesses, and assesses real-world impact. All testing is performed within the authorised boundaries and with reasonable care to minimise disruption to the production environment."}
        </Body>

        <SubHeading>Execution Lifecycle</SubHeading>
        <Table>
          <TableRow
            header
            cells={["Phase", "Description"]}
            widths={[2.2, 4.8]}
          />
          {fields?.executionLifecycle && fields.executionLifecycle.length > 0 ? (
            fields.executionLifecycle.map((row, idx) => (
              <TableRow
                key={`el-${idx}`}
                widths={[2.2, 4.8]}
                cells={[row.phase, row.description]}
                last={idx === fields.executionLifecycle!.length - 1}
              />
            ))
          ) : (
            <>
              <TableRow widths={[2.2, 4.8]} cells={["Reconnaissance & Target Validation", "Confirm authorised targets, access methods, test accounts, role configuration, and allowlisting. Map the authorised attack surface."]} />
              <TableRow widths={[2.2, 4.8]} cells={["Automated Vulnerability Analysis", "Identify candidate weaknesses across the authorised targets using automated tooling."]} />
              <TableRow widths={[2.2, 4.8]} cells={["Manual Penetration Testing", "Manual testing of authentication, session management, access control, business logic, privilege escalation between access tiers, and the payment and identity-verification paths within the agreed constraints."]} />
              <TableRow widths={[2.2, 4.8]} cells={["Exploitation Validation & Risk Assessment", "Safely validate exploitable findings, assess business impact, and assign risk ratings."]} />
              <TableRow widths={[2.2, 4.8]} cells={["Reporting & Remediation Guidance", "Document validated findings with evidence, severity, impact, and actionable remediation guidance."]} />
              <TableRow widths={[2.2, 4.8]} last cells={["Retesting", "Validate remediated findings on a per-finding basis as they are resolved by the Client, within the retesting period."]} />
            </>
          )}
        </Table>

        <SubHeading>Coverage Areas</SubHeading>
        {(() => {
          // Dynamically compute applicable standardised methodologies from displayTargets
          const matchingMethodologies = getMethodologiesForTargets(displayTargets);

          const getCustomBullets = (text?: string): string[] | null => {
            if (!text || !text.trim()) return null;
            return text
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean);
          };

          const webBullets = getCustomBullets(fields?.coverageWebText);
          const apiBullets = getCustomBullets(fields?.coverageApiText);
          const paymentBullets = getCustomBullets(fields?.coveragePaymentText);
          const netBullets = getCustomBullets(fields?.coverageNetworkText);

          // If no matching methodologies were computed from targets, but fields exist, build fallback list
          const list = matchingMethodologies.length > 0 ? matchingMethodologies : [
            ...(webBullets ? [{ id: "web-custom", category: "WEB" as const, title: "Web Application Security Testing", intro: "", bullets: [], outro: "" }] : []),
            ...(apiBullets ? [{ id: "api-custom", category: "API" as const, title: "API Security Testing", intro: "", bullets: [], outro: "" }] : []),
            ...(netBullets ? [{ id: "net-custom", category: "NETWORK" as const, title: "Network Security Testing", intro: "", bullets: [], outro: "" }] : [])
          ];

          if (list.length === 0 && !paymentBullets) {
            return null;
          }

          return (
            <View style={{ width: PAGE_WIDTH }}>
              {list.map((mod) => {
                let bulletsToRender = mod.bullets;
                if (mod.category === "WEB" && webBullets) {
                  bulletsToRender = webBullets;
                } else if (mod.category === "API" && apiBullets) {
                  bulletsToRender = apiBullets;
                } else if (mod.category === "NETWORK" && netBullets) {
                  bulletsToRender = netBullets;
                }

                return (
                  <View key={mod.id} style={{ width: PAGE_WIDTH, marginBottom: 14 }}>
                    <Text style={s.assetTitle}>{mod.title}</Text>
                    {mod.intro ? (
                      <Body style={{ marginBottom: 6 }}>{mod.intro}</Body>
                    ) : null}
                    {bulletsToRender.map((bullet, bIdx) => (
                      <Bullet key={`${mod.id}-b-${bIdx}`} text={bullet} />
                    ))}
                    {mod.outro ? (
                      <Body style={{ marginTop: 6 }}>{mod.outro}</Body>
                    ) : null}
                  </View>
                );
              })}

              {paymentBullets ? (
                <View key="payment-coverage-custom" style={{ width: PAGE_WIDTH, marginBottom: 14 }}>
                  <Text style={s.assetTitle}>Payment and Identity Verification Paths Coverage</Text>
                  {paymentBullets.map((bullet, bIdx) => (
                    <Bullet key={`pay-b-${bIdx}`} text={bullet} />
                  ))}
                </View>
              ) : null}
            </View>
          );
        })()}

        {/* ─── Timeline and Deliverables ─── */}
        <View style={s.docTitleBanner}>
          <Text style={s.docTitleText}>Timeline and Deliverables</Text>
        </View>

        <Table>
          <TableRow header cells={["Milestone", "Schedule"]} widths={[2.8, 4.2]} />
          {fields?.milestoneSchedule && fields.milestoneSchedule.length > 0 ? (
            fields.milestoneSchedule.map((m, idx) => (
              <TableRow
                key={`ms-${idx}`}
                widths={[2.8, 4.2]}
                cells={[m.milestone, m.schedule]}
                last={idx === fields.milestoneSchedule!.length - 1}
              />
            ))
          ) : (
            <>
              <TableRow widths={[2.8, 4.2]} cells={["Expected Engagement Initiation Date", "To be confirmed. Testing begins following execution of this Statement of Work, completion of Appendix A, receipt of payment, and the Client's confirmation that the platform is publicly available."]} />
              <TableRow widths={[2.8, 4.2]} cells={["Scope Validation, Target Finalisation & Completion of Appendix A", "1-2 business days"]} />
              <TableRow widths={[2.8, 4.2]} cells={["Testing Execution", fields?.testingStartDate ? `Scheduled: ${fmtDate(fields.testingStartDate)} to ${fmtDate(fields.testingEndDate)}` : "To be confirmed at scope validation and recorded in Appendix A"]} />
              <TableRow widths={[2.8, 4.2]} cells={["Analysis, Validation & Report Drafting", "2-3 business days"]} />
              <TableRow widths={[2.8, 4.2]} cells={["Draft Report Delivery", fields?.draftReportDate ? fmtDate(fields.draftReportDate) : "Within 3 business days of testing completion"]} />
              <TableRow widths={[2.8, 4.2]} cells={["Client Remediation Window", "To be confirmed by the Client"]} />
              <TableRow widths={[2.8, 4.2]} cells={["Retesting & Validation", fields?.retestingEndDate ? `On request until ${fmtDate(fields.retestingEndDate)}` : "On request, per finding, throughout the 90-day retesting period following the Project Completion Date"]} />
              <TableRow widths={[2.8, 4.2]} last cells={["Final Report Delivery", fields?.finalReportDate ? fmtDate(fields.finalReportDate) : "Within 2 business days of the Client confirming that remediation and associated retesting are complete"]} />
            </>
          )}
        </Table>

        <Body style={{ fontStyle: "italic", color: "#475569", marginTop: 4, marginBottom: 12 }}>
          {fields?.timelineAssumptionsText && fields.timelineAssumptionsText.trim()
            ? fields.timelineAssumptionsText
            : "This indicative timeline assumes timely provision of all required target information, access arrangements, test accounts, allowlisting, and environmental readiness by the Client, and confirmation of the date on which the platform becomes publicly available. Any material change to scope, target availability, or testing dependencies may require the timeline to be revised."}
        </Body>

        <SubHeading>Deliverables</SubHeading>
        <Table>
          <TableRow header cells={["Deliverable", "Description"]} widths={[2.5, 4.5]} />
          {fields?.deliverables && fields.deliverables.length > 0 ? (
            fields.deliverables.map((d, idx) => (
              <TableRow
                key={`del-${idx}`}
                widths={[2.5, 4.5]}
                cells={[d.deliverable, d.description]}
                last={idx === fields.deliverables!.length - 1}
              />
            ))
          ) : (
            <>
              <TableRow widths={[2.5, 4.5]} cells={["Draft Penetration Testing Report", "A draft report detailing all confirmed findings, severity ratings, supporting evidence, and prioritised remediation guidance, issued for Client review. Any real customer personal data appearing in evidence will be redacted or masked, with synthetic samples used where practical."]} />
              <TableRow widths={[2.5, 4.5]} cells={["Retesting Validation Output", "Confirmation of the retesting outcome for each remediated finding submitted for validation, recorded against the finding in the CTB platform. Retesting operates on a per-finding basis for the duration of the retesting period."]} />
              <TableRow widths={[2.5, 4.5]} cells={["Final Penetration Testing Report", "The finalised report incorporating Client feedback and the outcome of retesting, issued once the Client confirms that remediation and associated retesting are complete."]} />
              <TableRow widths={[2.5, 4.5]} last cells={["CTB Platform Access", "Access to the CTB platform for managing findings and tracking remediation during the engagement and for 90 days from the Project Completion Date, covering the full retesting period."]} />
            </>
          )}
        </Table>

        <SubHeading>CTB PTaaS Platform Delivery</SubHeading>
        {fields?.ptaasPlatformText && fields.ptaasPlatformText.trim() ? (
          fields.ptaasPlatformText.trim().split("\n\n").map((p, idx) => <Body key={`pt-${idx}`}>{p.trim()}</Body>)
        ) : (
          <>
            <Body>
              The Client receives access to the CTB platform from engagement commencement. The platform provides engagement visibility, a vulnerability dashboard with finding-level detail and evidence, remediation tracking, per-finding retest submission and outcomes, report artefacts, team access for relevant Client stakeholders, and centralised communication with CTB. Access remains available throughout testing, reporting, remediation coordination, and retesting, and for 90 days from the Project Completion Date, so that it covers the full retesting period during which findings are submitted for validation. Continued access after that point may require a separate agreement or subscription.
            </Body>
            <Body>
              Use of the CTB platform is governed by the CTB Customer Terms and Conditions incorporated into this Statement of Work. No separate platform agreement is required, and this Statement of Work takes precedence in the event of conflict.
            </Body>
          </>
        )}

        {/* ─── Assumptions and Client Dependencies ─── */}
        <View style={s.docTitleBanner}>
          <Text style={s.docTitleText}>Assumptions and Client Dependencies</Text>
        </View>

        <SubHeading>Assumptions</SubHeading>
        <Body>
          The following assumptions apply. Where an assumption proves incorrect, the scope, timeline, or fee may require revision:
        </Body>
        {fields?.assumptions && fields.assumptions.trim() ? (
          fields.assumptions
            .trim()
            .split("\n")
            .map((l) => l.replace(/^[•\-\*]\s*/, "").trim())
            .filter(Boolean)
            .map((line, i) => <Bullet key={`ass-${i}`} text={line} />)
        ) : (
          <View style={{ width: PAGE_WIDTH }}>
            <Bullet text="The platform is fully publicly available for the duration of the testing window." />
            <Bullet text="Testing is performed against the production environment, as directed by the Client." />
            <Bullet text="Workflows available to the standard access tier direct outbound communications to simulated recipients only, so testing of that tier will not generate live communications to third parties." />
            <Bullet text="Identity-verification functionality is exercised only against the Client's non-billable sandbox path; CTB will not perform identity verification against live or billable services." />
            <Bullet text="The page count, functional inventory, API endpoint inventory, and the services hosted on the authorised external network address have not been confirmed and will be established during scope validation. Representative sampling may be applied where functionality and security controls are substantially identical." />
            <Bullet text="No source code, secure code review, or architecture review is included." />
          </View>
        )}

        {fields?.clientDependenciesEnabled !== false ? (
          <>
            <SubHeading>Client Dependencies</SubHeading>
            <Body>The Client will:</Body>
            {fields?.clientDependencies && fields.clientDependencies.trim() ? (
              fields.clientDependencies
                .trim()
                .split("\n")
                .map((l) => l.replace(/^[•\-\*]\s*/, "").trim())
                .filter(Boolean)
                .map((line, i) => <Bullet key={`dep-${i}`} text={line} />)
            ) : (
              <View style={{ width: PAGE_WIDTH }}>
                <Bullet text="Confirm the date on which the platform becomes publicly available, so the testing window can be scheduled." />
                <Bullet text="Provision and maintain test accounts for the standard and privileged access tiers, release privileged credentials following execution of this Statement of Work, and provision additional accounts on request where required to complete the agreed coverage." />
                <Bullet text="Allowlist CTB's testing source address and confirm any required adjustments to web application firewall, bot protection, or rate-limiting controls." />
                <Bullet text="Ensure the payment path is testable end to end in the Client's Stripe test environment (test mode), covering the checkout, webhook, and settlement paths against Stripe test cards. No funds, payment instruments, or spend cap are required." />
                <Bullet text="Confirm the services hosted on the authorised external network address." />
                <Bullet text="Nominate an emergency contact available during the testing window, and confirm any blackout periods or change windows to be avoided." />
                <Bullet text="Provide a short product walkthrough so that testers understand the intended workflows before testing begins." />
                <Bullet text="Confirm remediation status and submit findings for retesting through the CTB platform during the retesting period." />
              </View>
            )}
            <Body style={{ marginTop: 6 }}>
              The specific values for these items - accounts, source address, spend cap, testing window, blackout periods, and emergency contacts - are recorded in Appendix A.
            </Body>
          </>
        ) : null}

        {/* ─── Disclaimer and Client Responsibilities ─── */}
        <View style={s.docTitleBanner}>
          <Text style={s.docTitleText}>Disclaimer and Client Responsibilities</Text>
        </View>

        {fields?.disclaimersText && fields.disclaimersText.trim() ? (
          fields.disclaimersText.trim().split("\n\n").map((para, idx) => (
            <Body key={`disc-${idx}`}>{para.trim()}</Body>
          ))
        ) : (
          <>
            <Text style={s.assetTitle}>Professional Conduct</Text>
            <Body>
              CTB will perform the services with reasonable skill, care, and diligence, consistent with recognised industry practices applicable to penetration testing engagements.
            </Body>

            <Text style={s.assetTitle}>Risk Acknowledgement</Text>
            <Body>
              Testing will be conducted against a production environment. While CTB takes reasonable care to minimise disruption, certain testing activities carry an inherent risk of service degradation or unintended effects, and testing may generate records, notifications, state changes, or transactions within the Client's environment. The Client acknowledges these risks and is responsible for maintaining appropriate backups and operational safeguards during the testing window.
            </Body>

            <Text style={s.assetTitle}>Issue Notification</Text>
            <Body>
              CTB will promptly notify the Client of any material unintended issue arising from testing, and of any critical vulnerability identified during the engagement that warrants immediate attention.
            </Body>

            <Text style={s.assetTitle}>Client Authority and Responsibility</Text>
            <Body>
              The Client confirms that it has the authority to authorise testing of the in-scope targets and is responsible for obtaining any third-party authorisations required, including from hosting, infrastructure, payment, identity-verification, or other service providers, before testing begins.
            </Body>

            <Text style={s.assetTitle}>Indemnity</Text>
            <Body>
              To the extent permitted by law, the Client will indemnify CTB, its employees, and contractors against third-party claims arising from the Client's lack of authority to approve testing of the in-scope targets, except to the extent such claims are finally determined to result from CTB's wilful misconduct or gross negligence.
            </Body>

            <Text style={s.assetTitle}>Confidentiality of Client Information</Text>
            <Body>
              CTB acknowledges that the platform and the information disclosed during this engagement include commercially sensitive material, including material relating to intellectual property that has not yet been granted. Such information will be handled in accordance with the confidentiality obligations agreed between the parties and the incorporated CTB Customer Terms and Conditions, and disclosed only to CTB personnel engaged in the delivery of this engagement.
            </Body>

            <Text style={s.assetTitle}>Data Handling and Retention</Text>
            <Body>
              CTB will securely destroy all Client data and testing artefacts within 30 days of issuing the final penetration testing report, and will retain the report for no longer than 12 months unless the parties agree otherwise in writing. A certificate of destruction will be provided on request.
            </Body>

            <Text style={s.assetTitle}>Breach Notification</Text>
            <Body>
              CTB will notify the Client within 72 hours of becoming aware of any security breach affecting Client data or information handled in connection with this engagement.
            </Body>

            <Text style={s.assetTitle}>Intellectual Property</Text>
            <Bullet text="All pre-existing intellectual property, methodologies, and tooling used in the delivery of the engagement remain the property of CTB." />
            <Bullet text="Upon full payment, the Client is granted a non-exclusive, perpetual licence to use the deliverables for its internal security, audit, compliance, and risk management purposes." />
            <Bullet text="The Client may share the deliverables with its auditors, regulators, customers, and professional advisors on a need-to-know basis." />
            <Bullet text="The deliverables may not be resold or commercially redistributed without CTB's prior written consent." />

            <Text style={s.assetTitle}>Limitation of Assurance</Text>
            <Body>
              The assessment provides a point-in-time evaluation of the in-scope targets. It does not constitute a warranty that the targets are free of all vulnerabilities, and no assurance of completeness is given or implied. Security posture may change as the environment, configuration, or threat landscape evolves.
            </Body>

            <Text style={s.assetTitle}>Governing Law and Jurisdiction</Text>
            <Body>
              This Statement of Work is governed by and construed in accordance with the laws of New Zealand, and the parties submit to the exclusive jurisdiction of the New Zealand courts.
            </Body>

            <Text style={s.assetTitle}>Applicable Policies and Terms</Text>
            <Body>
              This Statement of Work incorporates by reference, and is to be read together with, the following CTB documents, which form part of this engagement and are binding on the parties:
            </Body>
            <Bullet text="CTB Customer Terms and Conditions: https://capturethebug.xyz/Useful-Links/Customer-Terms&Conditions" />
            <Bullet text="Privacy Policy: https://capturethebug.xyz/Useful-Links/Privacy-Policy" />

            <Text style={s.assetTitle}>Order of Precedence</Text>
            <Body>
              This Statement of Work, including Appendix A - Rules of Engagement, and the CTB Customer Terms and Conditions are intended to operate together. Where there is a direct conflict, the order of precedence is: (a) the body of this Statement of Work; (b) Appendix A - Rules of Engagement; (c) the CTB Customer Terms and Conditions; and (d) any other document incorporated by reference. The body of this Statement of Work expressly prevails with respect to the timing of payment, the late-payment fee, the retesting entitlement, the agreed fee, and the Client's permitted use and sharing of the penetration testing report, and a field completed in Appendix A after execution cannot vary any of those matters.
            </Body>
          </>
        )}

        {/* ─── Exclusions ─── */}
        <SubHeading>Exclusions</SubHeading>
        <Body>Unless expressly agreed otherwise in writing, the following are excluded from this engagement:</Body>
        {fields?.standardExclusions && fields.standardExclusions.trim() ? (
          fields.standardExclusions
            .trim()
            .split("\n")
            .map((l) => l.replace(/^[•\-\*]\s*/, "").trim())
            .filter(Boolean)
            .map((line, i) => <Bullet key={`stex-${i}`} text={line} />)
        ) : (
          <>
            <Bullet text="Any application, environment, domain, subdomain, API, service, or address not explicitly identified as an authorised target." />
            <Bullet text="Internal networks, internal infrastructure, cloud infrastructure assessments, cloud configuration reviews, and underlying hosting platforms." />
            <Bullet text="The systems and infrastructure of third-party payment processors, identity-verification providers, and other third-party service providers." />
            <Bullet text="Testing of the payment path against live or billable transactions; all payment testing is limited to the Client's Stripe test environment (test mode)." />
            <Bullet text="Identity verification against live or billable services, and generation of live communications to real third-party recipients." />
            <Bullet text="Denial-of-service, distributed denial-of-service, stress testing, load testing, and other availability-focused testing activities." />
            <Bullet text="Physical security assessments, social engineering, phishing, vishing, and other human-focused security testing activities." />
            <Bullet text="Source code review, secure code review, software composition analysis, architecture review, and threat modelling activities." />
            <Bullet text="Formal compliance assessments, audits, certifications, or attestations against any regulatory or security framework." />
            <Bullet text="Testing of functionality, features, or environments introduced after scope validation unless separately agreed in writing." />
          </>
        )}
        {fields?.additionalExclusions && fields.additionalExclusions.trim() ? (
          fields.additionalExclusions
            .trim()
            .split("\n")
            .map((l) => l.replace(/^[•\-\*]\s*/, "").trim())
            .filter(Boolean)
            .map((line, i) => <Bullet key={`ex-${i}`} text={line} />)
        ) : null}

        {/* ─── Commercial Terms ─── */}
        <View style={s.docTitleBanner}>
          <Text style={s.docTitleText}>Commercial Terms</Text>
        </View>

        <Table>
          <TableRow header cells={["Item", "Description"]} widths={[2.5, 4.5]} />
          <TableRow widths={[2.5, 4.5]} cells={["Engagement Model", fields?.engagementModel === "recurring" ? "Recurring PTaaS Baseline Assessment" : fields?.engagementModel === "retainer" ? "Retainer Security Assessment" : "One-Time Baseline Comprehensive Assessment"]} />
          <TableRow widths={[2.5, 4.5]} cells={["Indicative Delivery Duration", fields?.indicativeDeliveryDuration || "To be confirmed following scope validation and confirmation of the final target, functional, API, and external service inventory, and recorded in Appendix A. This engagement is fixed-price; the confirmed duration does not represent a committed number of consultant-days."]} />
          <TableRow widths={[2.5, 4.5]} cells={["Payment Terms", fields?.paymentTerms || "100% of the engagement fee is payable upfront. The engagement is scheduled and commences on receipt of cleared funds."]} />
          <TableRow widths={[2.5, 4.5]} last cells={["Engagement Fee", price !== "To be confirmed" ? price : "TBC"]} />
        </Table>

        <SubHeading>Engagement Structure</SubHeading>
        <Table>
          <TableRow header cells={["Phase", "Description", "Indicative Duration"]} widths={[2.2, 3.6, 1.2]} />
          {fields?.engagementStructure && fields.engagementStructure.length > 0 ? (
            fields.engagementStructure.map((row, idx) => (
              <TableRow
                key={`es-${idx}`}
                widths={[2.2, 3.6, 1.2]}
                cells={[row.phase, row.description, row.duration]}
                last={idx === fields.engagementStructure!.length - 1}
              />
            ))
          ) : (
            <>
              <TableRow widths={[2.2, 3.6, 1.2]} cells={["Scope Confirmation and Setup", "Finalise targets, confirm hosted services, complete Appendix A, provision test accounts, confirm allowlisting", "1-2 business days"]} />
              <TableRow widths={[2.2, 3.6, 1.2]} cells={["Baseline Comprehensive Assessment", "Unauthenticated and authenticated grey-box testing of the authorised web applications, API and integration services, and external network host", "To be confirmed at scope validation"]} />
              <TableRow widths={[2.2, 3.6, 1.2]} cells={["Reporting & QA", "Draft report, quality assurance, and final report delivery", "2-3 business days"]} />
              <TableRow widths={[2.2, 3.6, 1.2]} cells={["Retesting", "Per-finding validation of remediated findings", "90 days from the Project Completion Date"]} />
              <TableRow widths={[2.2, 3.6, 1.2]} last cells={["Platform Access", "Access to the CTB platform for findings and remediation tracking", "Engagement plus 90 days from the Project Completion Date"]} />
            </>
          )}
        </Table>

        <SubHeading>General Terms</SubHeading>
        <Table>
          <TableRow header cells={["SN", "Term", "Details"]} widths={[0.5, 1.8, 4.7]} />
          {fields?.generalTerms && fields.generalTerms.length > 0 ? (
            fields.generalTerms.map((t, idx) => (
              <TableRow
                key={`gt-${idx}`}
                widths={[0.5, 1.8, 4.7]}
                cells={[t.sn || String(idx + 1), t.term, t.details]}
                last={idx === fields.generalTerms!.length - 1}
              />
            ))
          ) : (
            <>
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["1", "Services Scope", "CTB will perform the security assessment services described in Section 3 and Section 4 of this Statement of Work in respect of the authorised targets."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["2", "Authorisation & Authority", "The Client warrants that it has full right, power, and legal authority to authorise CTB to perform testing against the targets listed in Table 1."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["3", "Confidentiality", "Each party agrees to hold in confidence all non-public information received from the other party in connection with this Statement of Work."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["4", "Intellectual Property & Report Rights", "Upon full payment of the engagement fee, the Client is granted a non-exclusive, perpetual, worldwide license to use, copy, and internally distribute the penetration testing report."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["5", "Limitation of Liability", "To the maximum extent permitted by law, CTB's aggregate liability arising under or in connection with this Statement of Work is limited to the total fees paid by the Client under this Statement of Work."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["6", "Governing Law", "This Statement of Work is governed by and construed in accordance with the laws of New Zealand."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} last cells={["7", "Timeline Dependencies", "Timelines are indicative and depend on timely provision of access, credentials, allowlisting, and required information by the Client."]} />
            </>
          )}
        </Table>

        <SubHeading>Specific Terms</SubHeading>
        <Table>
          <TableRow header cells={["SN", "Term", "Details"]} widths={[0.5, 1.8, 4.7]} />
          {fields?.specificTerms && fields.specificTerms.length > 0 ? (
            fields.specificTerms.map((t, idx) => {
              const details =
                t.term && /payment/i.test(t.term) && fields?.paymentTerms && fields.paymentTerms.trim()
                  ? fields.paymentTerms
                  : t.details;

              return (
                <TableRow
                  key={`st-${idx}`}
                  widths={[0.5, 1.8, 4.7]}
                  cells={[t.sn || String(idx + 1), t.term, details]}
                  last={idx === fields.specificTerms!.length - 1}
                />
              );
            })
          ) : (
            <>
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["1", "Payment Terms", fields?.paymentTerms || "100% of the engagement fee is payable upfront. The engagement is scheduled and commences on receipt of cleared funds."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["2", "Engagement Commencement", "Testing commences once scope is finalised, Appendix A is completed and confirmed in writing by both parties, test accounts and allowlisting are confirmed, cleared funds are received, and the Client has confirmed that the platform is publicly available. Arrangements specific to live payment testing are not a precondition to the commencement of the engagement as a whole; see Live Payment Testing below."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["3", "Engagement Duration", "As set out in the Timeline and Deliverables and Engagement Structure sections, subject to scope dependencies."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["4", "Refund Policy", "Once testing has commenced, fees are non-refundable."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["5", "Retesting", "Unlimited retesting of findings reported during this engagement is included for 90 days following the Project Completion Date. For the purposes of this Statement of Work, the Project Completion Date is the date on which CTB issues the draft penetration testing report, unless a different date is agreed and recorded in Appendix A. Findings may be submitted for retesting individually as they are remediated, allowing a phased approach to remediation and validation. Retesting is limited to validating the remediation of findings reported in this engagement and does not extend to functionality, features, or targets introduced after scope validation."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} cells={["6", "Payment Testing", "Payment Testing: Testing of the payment path is performed exclusively in the Client's Stripe test environment (test mode), exercising the full checkout, webhook, and settlement paths using Stripe test cards. No real transaction value is involved; accordingly, no maximum aggregate transaction value, funding, or payment instruments are required from either party, and no live or billable transactions will be initiated."]} />
              <TableRow widths={[0.5, 1.8, 4.7]} last cells={["7", "Named Tester Access", "Administrative access is restricted to named CTB testers nominated by CTB and accepted by the Client. Credentials for the privileged access tier are released following execution of this Statement of Work."]} />
            </>
          )}
        </Table>

        {/* ─── Acceptance Page ─── */}
        <View style={s.docTitleBanner}>
          <Text style={s.docTitleText}>Acceptance</Text>
        </View>

        <Body>This Scope of Work becomes binding upon execution by both parties.</Body>

        <Table>
          <TableRow header cells={["Client Authorisation", " "]} widths={[2.3, 4.7]} />
          <TableRow header cells={["Client Information", "Details"]} widths={[2.3, 4.7]} />
          <TableRow widths={[2.3, 4.7]} cells={["Name:", fields?.clientSignerName || " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Signature:", " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Title:", fields?.clientSignerTitle || " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Phone:", fields?.clientPhone || " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Email:", fields?.clientEmail || " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Date:", fields?.clientSignedAt ? fmtDate(fields.clientSignedAt) : " "]} />
          
          <TableRow header cells={["Client Billing Details", " "]} widths={[2.3, 4.7]} />
          <TableRow widths={[2.3, 4.7]} cells={["Invoice Addressed To:", fields?.clientBillingName || " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Registered Company Name:", fields?.clientRegisteredCompany || " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Country of Incorporation:", fields?.clientCountryOfIncorporation || " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Billing Address:", fields?.clientBillingAddress || " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["GST / Tax / Company Registration Number:", fields?.clientTaxNumber || " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Billing Email:", fields?.clientBillingEmail || " "]} />
          <TableRow widths={[2.3, 4.7]} last cells={["Billing Contact Phone Number:", fields?.clientBillingPhone || " "]} />
        </Table>

        <Text style={[s.assetTitle, { marginTop: 16 }]}>CAPTURE THE BUG LIMITED</Text>
        <Table>
          <TableRow header cells={["CTB Information", "Details"]} widths={[2.3, 4.7]} />
          <TableRow widths={[2.3, 4.7]} cells={["Name", fields?.ctbSignerName || "Ankita Dhakar"]} />
          <TableRow widths={[2.3, 4.7]} cells={["Signature", fields?.ctbSignatureBase64 ? "[Digital Signature Attached]" : " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Title", fields?.ctbSignerTitle || "CEO"]} />
          <TableRow widths={[2.3, 4.7]} cells={["Phone", fields?.ctbSignerPhone || "+64-221994320"]} />
          <TableRow widths={[2.3, 4.7]} cells={["Email", fields?.ctbSignerEmail || "ankita@capturethebug.xyz"]} />
          <TableRow widths={[2.3, 4.7]} last cells={["Date", fields?.ctbSignedAt ? fmtDate(fields.ctbSignedAt) : " "]} />
        </Table>

        {/* ─── Appendix A — Rules of Engagement ─── */}
        <View style={s.docTitleBanner}>
          <Text style={s.docTitleText}>Appendix A - Rules of Engagement</Text>
        </View>

        <Body>
          {fields?.appendixRoEIntro && fields.appendixRoEIntro.trim()
            ? fields.appendixRoEIntro
            : "This Appendix forms part of this Statement of Work and sets out the operational rules under which testing is authorised and performed. Fields marked \"To be confirmed\" will be completed and confirmed in writing by both parties before testing commences; completion of this Appendix is a condition precedent to the commencement of testing. Where this Appendix conflicts with the body of this Statement of Work, the body prevails."}
        </Body>

        <SubHeading>Authorisation and Environment</SubHeading>
        <Table>
          <TableRow header cells={["Item", "Detail"]} widths={[2.3, 4.7]} />
          <TableRow widths={[2.3, 4.7]} cells={["Authorised Targets", `As listed in the Authorised Targets table: ${displayTargets.map((t: any) => t.targets || t.assetName).filter(Boolean).join(", ") || "Targets"}. No other host, service, application, or address is authorised for testing.`]} />
          <TableRow widths={[2.3, 4.7]} cells={["Services on Target Infrastructure", fields?.servicesOnInfrastructure || "To be confirmed before testing commences."]} />
          <TableRow widths={[2.3, 4.7]} cells={["Environment", `${fields?.testingEnvironment || "Production"}. Testing does not commence before the Client confirms the platform is fully publicly available.`]} />
          <TableRow widths={[2.3, 4.7]} cells={["Public Availability Date", fields?.publicAvailabilityDate || "To be confirmed by the Client."]} />
          <TableRow widths={[2.3, 4.7]} last cells={["Third-Party Authorisations", fields?.thirdPartyAuthorisationsText || "The Client confirms it has obtained any third-party approvals reasonably required for the authorised testing activities, including where applicable from hosting, infrastructure, payment, or identity-verification providers, before testing begins."]} />
        </Table>

        <SubHeading>Access and Testers</SubHeading>
        <Table>
          <TableRow header cells={["Item", "Detail"]} widths={[2.3, 4.7]} />
          <TableRow widths={[2.3, 4.7]} cells={["CTB Testing Source Address", `${fields?.testingSourceIp || "81.181.51.121/32"}, to be allowlisted by the Client for the duration of the engagement.`]} />
          <TableRow widths={[2.3, 4.7]} cells={["WAF / Rate Limiting", fields?.wafRateLimitingNote || "Controls that would materially impede testing to be adjusted or bypassed for the allowlisted source by agreement. Detail to be confirmed."]} />
          <TableRow widths={[2.3, 4.7]} cells={["Standard Access Account", `${fields?.standardAccessAccount || "pentest@capturethebug.xyz"} - standard user account, provisioned by the Client.`]} />
          <TableRow widths={[2.3, 4.7]} cells={["Privileged Access Account", `${fields?.privilegedAccessAccount || "jk@capturethebug.xyz"} - named administrative account, provisioned by the Client and released following execution of this Statement of Work.`]} />
          <TableRow widths={[2.3, 4.7]} last cells={["Additional Accounts", fields?.testAccountsNote || "Provisioned by the Client on request where required to complete the agreed coverage."]} />
        </Table>

        <SubHeading>Schedule</SubHeading>
        <Table>
          <TableRow header cells={["Item", "Detail"]} widths={[2.3, 4.7]} />
          <TableRow widths={[2.3, 4.7]} cells={["Confirmed Testing Duration", fields?.baselineAssessmentDuration || fields?.indicativeDeliveryDuration || "To be confirmed following scope validation."]} />
          <TableRow widths={[2.3, 4.7]} cells={["Testing Window", fields?.testingWindow || "To be confirmed before testing commences."]} />
          <TableRow widths={[2.3, 4.7]} cells={["Blackout Periods", fields?.blackoutPeriods || "To be confirmed before testing commences."]} />
          <TableRow widths={[2.3, 4.7]} last cells={["Project Completion Date", fields?.projectCompletionDateNote || "The date on which CTB issues the draft penetration testing report, unless a different date is agreed and recorded here."]} />
        </Table>

        <SubHeading>Payment and Identity Verification</SubHeading>
        <Table>
          <TableRow header cells={["Item", "Detail"]} widths={[2.3, 4.7]} />
          <TableRow widths={[2.3, 4.7]} cells={["Payment Testing", fields?.paymentTestingNote || "Performed exclusively in the Client's Stripe test environment (test mode), exercising the full checkout, webhook, and settlement paths using Stripe test cards. No real transaction value; no spend cap, funding, or test-transaction handling arrangements are required."]} />
          <TableRow widths={[2.3, 4.7]} last cells={["Identity Verification", fields?.identityVerificationNote || "Exercised only against the Client's non-billable sandbox path."]} />
        </Table>

        <SubHeading>Emergency Contacts</SubHeading>
        <Table>
          <TableRow header cells={["Party", "Contact Details"]} widths={[2.3, 4.7]} />
          <TableRow widths={[2.3, 4.7]} cells={["Client Emergency Contact", fields?.clientEmergencyContact || "To be confirmed - name, role, phone, email. Must be reachable throughout the testing window."]} />
          <TableRow widths={[2.3, 4.7]} last cells={["CTB Engagement Lead", fields?.ctbEngagementLead || "To be confirmed - name, phone, email."]} />
        </Table>

        <SubHeading>Prohibited Testing Activities</SubHeading>
        <Body>In addition to the activities listed under Exclusions, which are prohibited during this engagement, CTB will not:</Body>
        {fields?.prohibitedActivitiesText && fields.prohibitedActivitiesText.trim() ? (
          fields.prohibitedActivitiesText
            .trim()
            .split("\n")
            .map((l) => l.replace(/^[•\-\*]\s*/, "").trim())
            .filter(Boolean)
            .map((line, i) => <Bullet key={`pa-${i}`} text={line} />)
        ) : (
          <>
            <Bullet text="Intentionally destroy, encrypt, bulk-download, or otherwise remove Client production data from the Client environment, except for the minimum evidence reasonably necessary to demonstrate and document a confirmed finding, which will be recorded within the finding." />
            <Bullet text="Modify or delete Client user accounts, records, or configuration, other than accounts and records created by CTB for testing purposes." />
            <Bullet text="Install persistence mechanisms, backdoors, or any change intended to survive the testing window. Any test artefact created by CTB will be recorded and removed at the conclusion of testing." />
            <Bullet text="Use credentials, tokens, or access obtained during testing beyond what is required to demonstrate the finding." />
            <Bullet text="Test any host, service, application, or address not listed as an authorised target." />
          </>
        )}

        <SubHeading>Stop-Testing and Emergency Procedure</SubHeading>
        {fields?.stopTestingProcedureText && fields.stopTestingProcedureText.trim() ? (
          fields.stopTestingProcedureText
            .trim()
            .split("\n")
            .map((l) => l.replace(/^[•\-\*]\s*/, "").trim())
            .filter(Boolean)
            .map((line, i) => <Bullet key={`stp-${i}`} text={line} />)
        ) : (
          <>
            <Bullet text="Requesting a pause. Either party may request an immediate pause at any time by telephoning the other party's emergency contact, followed by written confirmation by email." />
            <Bullet text="CTB response. CTB will suspend all testing activity immediately on receipt of a pause request and confirm suspension in writing." />
            <Bullet text="CTB-initiated pause. CTB will pause testing on its own initiative, and notify the Client without delay, where it identifies a material unintended effect on the production environment, a critical vulnerability warranting immediate attention, or evidence of a pre-existing compromise." />
            <Bullet text="Resumption. Testing resumes only once both parties have agreed in writing, and only against the targets confirmed as ready." />
            <Bullet text="Effect on scope and timeline. Time lost to a pause does not reduce the agreed scope. The timeline will be adjusted in accordance with the Timeline Dependencies term." />
          </>
        )}

        <SubHeading>Confirmation of Appendix A</SubHeading>
        <Body>
          By confirming below, both parties agree that the fields marked "To be confirmed" have been completed and that testing may commence in accordance with this Appendix and the body of this Statement of Work.
        </Body>

        <Table>
          <TableRow header cells={["Client Confirmation", "Details"]} widths={[2.3, 4.7]} />
          <TableRow widths={[2.3, 4.7]} cells={["Name", fields?.clientSignerName || " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Title", fields?.clientSignerTitle || " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Signature", " "]} />
          <TableRow widths={[2.3, 4.7]} last cells={["Date", fields?.clientSignedAt ? fmtDate(fields.clientSignedAt) : " "]} />
        </Table>

        <Text style={[s.assetTitle, { marginTop: 16 }]}>CAPTURE THE BUG LIMITED</Text>
        <Table>
          <TableRow header cells={["CTB Confirmation", "Details"]} widths={[2.3, 4.7]} />
          <TableRow widths={[2.3, 4.7]} cells={["Name", fields?.ctbSignerName || "Ankita Dhakar"]} />
          <TableRow widths={[2.3, 4.7]} cells={["Signature", fields?.ctbSignatureBase64 ? "[Digital Signature Attached]" : " "]} />
          <TableRow widths={[2.3, 4.7]} cells={["Title", fields?.ctbSignerTitle || "CEO"]} />
          <TableRow widths={[2.3, 4.7]} cells={["Phone", fields?.ctbSignerPhone || "+64-221994320"]} />
          <TableRow widths={[2.3, 4.7]} cells={["Email", fields?.ctbSignerEmail || "ankita@capturethebug.xyz"]} />
          <TableRow widths={[2.3, 4.7]} last cells={["Date", fields?.ctbSignedAt ? fmtDate(fields.ctbSignedAt) : " "]} />
        </Table>

        {/* Fixed Footer */}
        <View style={s.footer} fixed>
          <Text style={s.footerLeft}>
            {footerLeftNote}
          </Text>
          <Text
            style={s.footerRight}
            render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
              `${pageNumber} / ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};
